import { useEffect } from 'react';
import { Moon, Sun, Cpu, Info } from 'lucide-react';
import { useThemeStore } from '@/shared/stores/theme';
import { useModelStore } from '@/shared/stores/model';
import { apiClient } from '@/shared/api/client';
import { toast } from 'sonner';

interface HealthResponse {
  status: string;
  provider: 'ollama' | 'deepseek';
  model: string;
  availableModels: string[];
}

export function SettingsTab(): JSX.Element {
  const { theme, toggleTheme } = useThemeStore();
  const { model, provider, availableModels, setModel, setConfig } = useModelStore();

  useEffect(() => {
    apiClient<HealthResponse>('/health')
      .then((data) => {
        setConfig({
          provider: data.provider,
          model: data.model,
          availableModels: data.availableModels,
        });
      })
      .catch(() => {
        toast.error('Не удалось загрузить конфигурацию LLM');
      });
  }, [setConfig]);

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-black/10 bg-white/50 p-4 dark:border-white/10 dark:bg-slate-900/50">
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">Оформление</h2>
        <button
          onClick={toggleTheme}
          className="flex w-full items-center justify-between rounded-xl bg-black/5 px-4 py-3 text-sm text-slate-700 transition-colors hover:bg-black/10 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
        >
          <span className="flex items-center gap-3">
            {theme === 'dark' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            {theme === 'dark' ? 'Тёмная тема' : 'Светлая тема'}
          </span>
          <span className="text-xs text-slate-500">Нажмите для переключения</span>
        </button>
      </div>

      <div className="rounded-2xl border border-black/10 bg-white/50 p-4 dark:border-white/10 dark:bg-slate-900/50">
        <h2 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">Провайдер LLM</h2>
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
          {provider === 'ollama' ? 'Локальная Ollama' : 'DeepSeek API'}
        </p>

        <h3 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">Модель</h3>
        <div className="grid grid-cols-2 gap-2">
          {availableModels.map((modelName) => (
            <button
              key={modelName}
              onClick={() => setModel(modelName)}
              className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-sm font-medium transition-all ${
                model === modelName
                  ? 'border-pink-500/50 bg-pink-500/10 text-pink-700 dark:text-pink-300'
                  : 'border-black/10 bg-white/50 text-slate-600 hover:border-black/20 dark:border-white/10 dark:bg-white/5 dark:text-slate-400 dark:hover:border-white/20'
              }`}
            >
              <Cpu className="h-4 w-4" />
              <span className="truncate">{modelName}</span>
            </button>
          ))}
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Изменение применится при следующей генерации.
        </p>
      </div>

      <div className="rounded-2xl border border-black/10 bg-white/50 p-4 dark:border-white/10 dark:bg-slate-900/50">
        <h2 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">О сервисе</h2>
        <div className="flex items-start gap-3 text-sm text-slate-600 dark:text-slate-400">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Mixly — сервис составления миксов для коктейлей и кальянов с помощью языковой
            модели.
          </p>
        </div>
        <p className="mt-3 text-xs text-slate-600">Версия 0.1.0</p>
      </div>
    </div>
  );
}
